import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  Modal,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloseIcon from '@mui/icons-material/Close';
import { MuiIconPicker } from './MuiIconPicker';
import * as MuiIcons from '@mui/icons-material';

interface CategoryFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (categoryName: string, icon: string) => Promise<void>;
  loading: boolean;
}

const CategoryForm: React.FC<CategoryFormProps> = ({
  open,
  onClose,
  onSubmit,
  loading,
}) => {
  const [categoryName, setCategoryName] = useState<string>('');
  const [selectedIcon, setSelectedIcon] = useState<string>('');
  const isMobile = useMediaQuery('(max-width:600px)');
  const { t } = useTranslation();
  const [missingName, setMissingName] = useState<boolean>(false);

  const handleCategorySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (categoryName == '') {
      setMissingName(true);
      return;
    }
    await onSubmit(categoryName, selectedIcon);
    setCategoryName('');
    setSelectedIcon('');
    setMissingName(false);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="parent-modal-title"
      aria-describedby="parent-modal-description"
    >
      <Box
        sx={{
          width: isMobile ? '85%' : 400, // Fixed width for the modal
          backgroundColor: 'white',
          margin: 'auto',
          padding: 2,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          borderRadius: '8px',
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#333',
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography component="h1" variant="h5">
          {t('poiForm.addCategory')}
        </Typography>
        <Box
          component="form"
          onSubmit={handleCategorySubmit}
          noValidate
          sx={{ mt: 1, width: '100%' }} // Ensure the form takes full width
        >
          <TextField
            margin="normal"
            fullWidth
            label={t('poiForm.name')}
            name="name"
            autoComplete="name"
            autoFocus
            type="text"
            id="name"
            value={categoryName}
            onChange={(e) => setCategoryName(e.target.value)}
          />
          {missingName && (
            <Box sx={{ width: '100%', display: 'flex', ml: 1 }}>
              <Typography sx={{ color: 'red', mb: 0.5 }}>
                {t('poiForm.missingName')}
              </Typography>
            </Box>
          )}
          <Box sx={{ mt: 2 }}>
            <MuiIconPicker
              onSelect={(icon) => setSelectedIcon(icon)}
              icons={MuiIcons}
            />
          </Box>
          <Button
            type="submit"
            fullWidth
            variant="contained"
            sx={{ mt: 3, mb: 2 }}
            disabled={loading}
          >
            {t('poiForm.save')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default CategoryForm;
